import { isEmpty } from "lodash";
import { z } from "zod";

import { Logger } from "@moment/logging";

import {
	type DocChangePermRequest,
	type Invitation,
	InviteResultSchema,
	type PermRelation,
	type SearchUserResult,
} from "~/state/types";

const logger = new Logger("docsapi");

export const getDocsApiUrl = (): string => {
	// set NEXT_PUBLIC_GITEA_API_URL env to  "localhost:8088" if you want to use local backend.
	const giteaApiUrl = process.env["NEXT_PUBLIC_GITEA_API_URL"];

	if (typeof giteaApiUrl === "string" && !isEmpty(giteaApiUrl)) {
		if (giteaApiUrl.startsWith("http")) {
			return giteaApiUrl;
		}
		return `https://${giteaApiUrl}`;
	}

	if (process.env.APP_ENV === "production") {
		return "https://git.moment.dev";
	}

	return "https://git-staging.moment.dev";
};

export const getDocsApiUrlWithoutProtocol = (): string => {
	const apiUrl = getDocsApiUrl();
	return apiUrl.replace(/^https?:\/\//, "");
};

export const getDocsWebsite = (): string => {
	const apiUrl = getDocsApiUrl();
	return apiUrl.replace(/^https:\/\/git/, "https://docs");
};

export function docsApiFetch(token: string, autothrow: boolean = true) {
	return async (
		input: string | URL | globalThis.Request,
		init?: RequestInit
	): Promise<Response> => {
		const headers = {
			...init?.headers,
		};

		if (!isEmpty(token)) {
			headers["Authorization"] = `Bearer ${token}`;
			headers["Content-Type"] = "application/json";
		}

		const response = await fetch(input, {
			...init,
			headers,
		});

		if (!response.ok && autothrow) {
			logger.error(`Failed to fetch ${input}`, { response });
			throw new Error(response.statusText, { cause: response });
		}

		return response;
	};
}

// this is used to fetch from docsapi endpoints, with prefix /internal/v1
export const createDocsApiFetch = (token: string, autothrow: boolean = true) => {
	return (path: string, init: RequestInit) => {
		const url = `${getDocsApiUrl()}/internal/v1${path}`;
		const fetch = docsApiFetch(token, autothrow);
		return fetch(url, init).then((response) => {
			return response.json();
		});
	};
};

export const querySearchUsers = async (
	token: string,
	query: string
): Promise<Array<SearchUserResult>> => {
	const docsapiClient = createDocsApiFetch(token);
	const params = new URLSearchParams();
	params.append("q", query);
	const users = await docsapiClient(`/query/users?${params.toString()}`, { method: "GET" });

	return users;
};

export const queryDocumentPermissions = async (
	token: string,
	workspaceName: string,
	documentID: string,
	permission: string
): Promise<Array<SearchUserResult>> => {
	const docsapiClient = createDocsApiFetch(token);
	const params = new URLSearchParams();
	params.append("permission", permission);
	const users = await docsapiClient(
		`/permissions/${workspaceName}/${documentID}/info?${params.toString()}`,
		{ method: "GET" }
	);

	return users;
};

export const queryInvitations = async (
	token: string,
	workspaceName: string,
	documentID: string
): Promise<Array<Invitation>> => {
	const docsapiClient = createDocsApiFetch(token);
	const invites = await docsapiClient(`/permissions/${workspaceName}/${documentID}/invites`, {
		method: "GET",
	});

	const schema = z.array(InviteResultSchema);

	const results = schema.parse(invites);

	return results;
};

export const updatePublicAccess = async (
	token: string,
	type: "grant" | "revoke",
	workspaceName: string,
	documentId: string,
	relation: PermRelation,
	logged_in_users_only: boolean = false
) => {
	const body: DocChangePermRequest = {
		userType: "AllUsers",
		relation,
		value: "",
	};

	if (logged_in_users_only) {
		body.userType = "LoggedInUsers";
	}

	await doUpdate(token, `/permissions/${workspaceName}/${documentId}?all=true`, type, body);
};

export const changePermission = async (
	token: string,
	workspaceName: string,
	documentID: string,
	type: "grant" | "revoke",
	relation: PermRelation,
	user: SearchUserResult
) => {
	const body: DocChangePermRequest = {
		userType: "Username",
		relation,
		value: user.nickname,
	};

	await doUpdate(token, `/permissions/${workspaceName}/${documentID}`, type, body);
};

export const updateInvite = async (
	token: string,
	workspaceName: string,
	documentID: string,
	type: "grant" | "revoke",
	relation: PermRelation,
	email: string
) => {
	const body: DocChangePermRequest = {
		userType: "Email",
		relation,
		value: email,
	};

	if (relation === "owner") {
		throw new Error("Cannot invite a new user as an owner");
	}

	await doUpdate(token, `/permissions/${workspaceName}/${documentID}`, type, body);
};

const doUpdate = async (
	token: string,
	url: string,
	type: "grant" | "revoke",
	body: DocChangePermRequest
): Promise<void> => {
	const method = type === "grant" ? "POST" : "DELETE";

	const docsapiClient = createDocsApiFetch(token, true);
	const result = await docsapiClient(url, {
		method,
		body: JSON.stringify(body),
	});
	if (result !== "OK") {
		throw new Error("Failed to change permission", result);
	}
};
